import React, { createRef, useState } from "react";
import { array, either, identity, option } from "fp-ts";
import { pipe } from "fp-ts/lib/pipeable";
import { CRMRoadblockTableList } from "../CRMRoadblockTableList/CRMRoadblockTableList";
import { CRMBlockScrollCacheView, CRMBlockScrollResetView } from "../../CRM/CRMBlock/CRMBlockViews/CRMBlockViews";
import { RoadblockForm, TRoadblockCheckListItemManualForm, TRoadblockEvidenceLink, TRoadblockForm, TRoadblockSignOffForm, TRoadblocksForm2, TRoadblocksListItem2 } from "../../../../../domain/codecs/form/RoadblockForm";
import { CRMRoadblockViewChat } from "../CRMRoadblockViewChat/CRMRoadblockViewChat";
import { TChatsMessageForm } from "../../../../../domain/codecs/form/ChatsMessageForm";
import { useCallbackOnUniquePropChange } from "../../../hooks/useCallbackOnUniquePropChange";
import { TBlockType } from "../../../state/router/routerTypes";
import { CRMRoadblockViewChecklist } from "../CRMRoadblockViewChecklist/CRMRoadblockViewChecklist";
import { PlainUser_NameCopy, TSessionUser } from "../../../../../domain/codecs/User";
import { roadblockDetailViewFromCaseForm } from "../../../models/RoadblockDetailView";
import { roadblockListViewFromCaseItem } from "../../../models/RoadblockListView";

type TCRMRoadblockBlockProps = {
    sessionUser: TSessionUser;
    openRoadblockID?: string;
    openRoadblockView?: string;
    roadblocksForm: TRoadblocksForm2;
    onOpen: (roadblock: TRoadblocksListItem2) => void;
    onClose: () => void;
    onChange: (form: TRoadblockForm) => void;
    onRefresh: (form: TRoadblockForm) => void;
    onChangeManualItem: (form: TRoadblockCheckListItemManualForm) => void;
    onTypeMessage: (form: TChatsMessageForm) => void;
    onSubmitMessage: (form: TChatsMessageForm) => void;
    onOpenForm: (target: TBlockType) => void;
    onOpenEvidenceLink: (link: TRoadblockEvidenceLink) => void;
    onSignOff: (form: TRoadblockSignOffForm) => void;
    onAssign: (payload: { roadblock_id: string, assigned_to_user_id: string }) => void;
    onAwait: (payload: { roadblock_id: string, awaitable_entities_id: string, days_override: number | null }) => void;
    onCancelAwait: (payload: { roadblock_id: string }) => void;
}

export const CRMRoadblocksBlock = (props: TCRMRoadblockBlockProps): JSX.Element => {

    const SingleViewRef = createRef<HTMLDivElement>();
    const Roadblocks = props.roadblocksForm.children.roadblocks;
    
    const SessionUserName = pipe(
        PlainUser_NameCopy.decode(props.sessionUser),
        either.fold(() => "", identity.flatten)
    );
    
    pipe(
        [props.sessionUser.first_name, props.sessionUser.last_name],
        array.map((name) => name.trim()),
        array.filter((name) => name !== ""),
        (a) => a.join(" "),
    );

    const [openRoadblockIndex, setRoadblockIndex] = useState(-1);
    const [singleViewMode, setSingleViewMode] = useState<"chat" | "checklist">("checklist");

    useCallbackOnUniquePropChange(
        (status) => {
            if (status === "success") {
                openRoadblockCachedInTheUrl();
            }
        },
        props.roadblocksForm.status
    );

    useCallbackOnUniquePropChange(
        () => openRoadblockCachedInTheUrl(),
        props.openRoadblockID
    );

    const openRoadblockCachedInTheUrl = () => pipe(
        Roadblocks,
        array.findIndex((roadblock) => roadblock.id === props.openRoadblockID),
        option.fold(
            () => undefined,
            (index) => onOpen(Roadblocks[index], props.openRoadblockView === "chat" ? "chat" : "checklist")
        )
    );

    const onOpen = (roadblock: TRoadblocksListItem2, view: "chat" | "checklist" = "checklist") => {
        setRoadblockIndex(findRoadblockIndexByID(roadblock.id));
        setSingleViewMode(view);
        props.onOpen(roadblock);
    }

    const findRoadblockIndexByID = (id: string) => pipe(
        Roadblocks,
        array.findIndex((roadblock) => roadblock.id === id),
        option.fold(
            () => -1,
            identity.flatten
        )
    )

    const onClose = () => {
        props.onClose();
        setRoadblockIndex(-1);
    }

    const onOpenChat = () => setSingleViewMode("chat");

    const onOpenChecklist = () => setSingleViewMode("checklist");

    const isARoadblockOpen = () => openRoadblockIndex > -1;

    const getOpenRoadblockOption = (): option.None | option.Some<TRoadblocksListItem2> =>
        !!Roadblocks[openRoadblockIndex]
            ? option.some(Roadblocks[openRoadblockIndex])
            : option.none
    ;

    const getOpenRoadblockDetailed = () => pipe(
        getOpenRoadblockOption(),
        option.fold(
            () => RoadblockForm.newDefault(),
            (item) => item.detailed_roadblock_form
        )
    );

    return (
        <>
            {/* TABLE LIST */}
            <CRMBlockScrollCacheView isShowing={!isARoadblockOpen()}>
                <CRMRoadblockTableList
                    roadblocks={Roadblocks.map(roadblockListViewFromCaseItem)}
                    assignableUsers={props.roadblocksForm.children.assignable_users}
                    awaitableEntities={props.roadblocksForm.children.awaitable_entities}
                    onClick={(roadblockId) => pipe(
                        Roadblocks,
                        array.findFirst((roadblock) => roadblock.id === roadblockId),
                        option.fold(
                            () => undefined,
                            (roadblock) => onOpen(roadblock)
                        )
                    )}
                    onAssign={props.onAssign}
                    onAwait={props.onAwait}
                    onCancelAwait={props.onCancelAwait}
                />
            </CRMBlockScrollCacheView>
            
            {/* SINGLE VIEW - CHECKLIST */}
            <CRMBlockScrollResetView 
                elementRef={SingleViewRef}
                isShowing={isARoadblockOpen() && singleViewMode === "checklist"}
            >
                <CRMRoadblockViewChecklist
                    sessionUser={props.sessionUser}
                    view={roadblockDetailViewFromCaseForm(getOpenRoadblockDetailed())}
                    onClose={onClose}
                    onOpenChat={onOpenChat}
                    onToggleManualItem={(change) => {
                        const detailed = getOpenRoadblockDetailed();
                        const updated = detailed.children.checklist_items_manual.map((item) =>
                            item.edited.id === change.id
                                ? {...item, edited: {...item.edited, ...change}}
                                : item
                        );

                        props.onChange({
                            ...detailed,
                            children: {...detailed.children, checklist_items_manual: updated},
                        });

                        const changed = updated.find((item) => item.edited.id === change.id);
                        if (changed) {
                            props.onChangeManualItem(changed);
                        }
                    }}
                    onRefresh={() => props.onRefresh(getOpenRoadblockDetailed())}
                    onSignOff={props.onSignOff}
                    onOpenForm={props.onOpenForm}
                    onOpenEvidenceLink={props.onOpenEvidenceLink}
                />
            </CRMBlockScrollResetView>

            {/* SINGLE VIEW - CHAT */}
            <CRMBlockScrollResetView 
                elementRef={SingleViewRef}
                isShowing={isARoadblockOpen() && singleViewMode === "chat"}
            >
                <CRMRoadblockViewChat
                    sessionUserName={SessionUserName}
                    parentRef={SingleViewRef}
                    view={roadblockDetailViewFromCaseForm(getOpenRoadblockDetailed())}
                    onTypeMessage={props.onTypeMessage}
                    onSubmitMessage={props.onSubmitMessage}
                    onClose={onClose}
                    onOpenForm={props.onOpenForm}
                    onOpenCheckList={onOpenChecklist}
                />
            </CRMBlockScrollResetView>
        </>
    );
}
